import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  AlertCircle, 
  CheckCircle,
  MapPin,
  User,
  Calendar,
  FileText,
  Image as ImageIcon,
  Search,
  UserCircle,
  Phone,
  Mail,
  Home
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { mobileClasses, isMobile, formatForMobile } from '@/utils/mobileUtils';

interface ComplaintCategory {
  id: string;
  name: string;
  code: string;
  description?: string;
  type: 'complaint' | 'enquiry' | 'suggestion' | 'maintenance';
  priority_level: number;
  estimated_resolution_time_hours: number;
  requires_approval: boolean;
  required_fields?: any;
}

interface MemberDetails {
  id: string;
  member_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  whatsapp_number?: string;
  block_number?: string;
  flat_number?: string;
  address?: string;
}

interface FormData {
  title: string;
  description: string;
  category_id: string;
  department_id: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  location: string;
  address: string; // Full address for complaint
  use_member_address: boolean; // Use member's address
  contact_phone: string; // Contact phone number
  use_member_phone: boolean; // Use member's phone
  preferred_contact_time?: string;
  attachments?: File[];
  user_id: string;
}

interface FormErrors {
  title?: string;
  description?: string;
  category_id?: string;
  department_id?: string;
  priority?: string;
  location?: string;
  general?: string;
  memberLookup?: string;
}

const NewComplaintPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<'lookup' | 'form' | 'success'>('lookup');
  const [memberIdentifier, setMemberIdentifier] = useState('');
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
  const [isLookingUpMember, setIsLookingUpMember] = useState(false);
  const [categories, setCategories] = useState<ComplaintCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complaintNumber, setComplaintNumber] = useState('');
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    category_id: '',
    department_id: '',
    priority: 'medium',
    location: '',
    address: '',
    use_member_address: true, // Default to using member's address
    contact_phone: '',
    use_member_phone: true, // Default to using member's phone
    preferred_contact_time: '',
    attachments: [],
    user_id: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<Array<{id: string; name: string; code: string}>>([]);

  // Fetch categories when form step is reached
  useEffect(() => {
    if (step === 'form') {
      fetchCategories();
    }
  }, [step]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      } else {
        console.error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!memberIdentifier.trim()) {
      setErrors({ memberLookup: 'Please enter Member ID or Phone Number' });
      return;
    }

    setIsLookingUpMember(true);
    setErrors({});

    try {
      const response = await fetch(`/api/v1/users/lookup/${encodeURIComponent(memberIdentifier.trim())}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const member = result.data;
        setMemberDetails(member);
        // Build member's full address
        const memberAddress = member.block_number && member.flat_number 
          ? `Block ${member.block_number}, House ${member.flat_number}${member.address ? `, ${member.address}` : ''}`
          : member.address || '';
        
        setFormData(prev => ({
          ...prev,
          user_id: member.id,
          contact_phone: member.phone || '',
          address: memberAddress,
          location: memberAddress, // Set location to member's address by default
          use_member_address: true,
          use_member_phone: true
        }));
        setStep('form');
      } else {
        const errorData = await response.json();
        setErrors({ 
          memberLookup: errorData.message || 'Member not found. Please check the Member ID or Phone Number.' 
        });
      }
    } catch (error) {
      console.error('Error looking up member:', error);
      setErrors({ 
        memberLookup: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsLookingUpMember(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters long';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Please select a category';
    }

    if (!formData.department_id) {
      newErrors.department_id = 'Please select a department';
    }

    // Validate address - either use member's address or provide custom address
    if (!formData.use_member_address && !formData.address.trim()) {
      newErrors.location = 'Address is required';
    }
    
    // Validate contact phone - either use member's phone or provide custom phone
    if (!formData.use_member_phone && !formData.contact_phone.trim()) {
      newErrors.general = 'Contact phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // When category changes, update available departments and reset department_id
      if (field === 'category_id') {
        const selectedCategory = categories.find(cat => cat.id === value);
        if (selectedCategory && selectedCategory.departments) {
          setAvailableDepartments(selectedCategory.departments);
          // Reset department_id if it's not in the new category's departments
          if (newData.department_id && !selectedCategory.departments.find(d => d.id === newData.department_id)) {
            newData.department_id = '';
          }
        } else {
          setAvailableDepartments([]);
          newData.department_id = '';
        }
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
      
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} has an unsupported format.`);
        return false;
      }
      
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setFormData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...validFiles]
    }));
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Determine final address and phone
      const finalAddress = formData.use_member_address && memberDetails
        ? formData.address // Member's address (already set from member lookup)
        : formData.address.trim();
      
      const finalContactPhone = formData.use_member_phone && memberDetails
        ? (memberDetails.phone || '')
        : formData.contact_phone.trim();

      // Prepare form data for submission
      const submitData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category_id: formData.category_id,
        department_id: formData.department_id,
        priority: formData.priority,
        location: finalAddress, // Use the determined address as location
        address: finalAddress,
        use_member_address: formData.use_member_address,
        contact_phone: finalContactPhone,
        use_member_phone: formData.use_member_phone,
        user_id: formData.user_id
      };

      // Only include contact_phone if provided
      if (!submitData.contact_phone) {
        delete submitData.contact_phone;
      }

      const response = await fetch('/api/v1/complaints', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        const complaintId = result.data.id;
        setComplaintNumber(result.data.complaint_number);
        
        // Upload files if any
        if (selectedFiles.length > 0) {
          try {
            const formDataToUpload = new FormData();
            selectedFiles.forEach(file => {
              formDataToUpload.append('files', file);
            });
            
            const uploadResponse = await fetch(`/api/v1/file-attachments/complaint/${complaintId}/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: formDataToUpload
            });
            
            if (!uploadResponse.ok) {
              console.warn('Files uploaded but complaint was created successfully');
            }
          } catch (uploadError) {
            console.warn('Error uploading files:', uploadError);
            // Don't fail the complaint creation if file upload fails
          }
        }
        
        setStep('success');
      } else {
        const errorData = await response.json();
        
        // Format error message - handle multiple errors or single error
        let errorMessage = 'Failed to submit complaint. Please try again.';
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.map((err: any) => err.msg || err.message).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        setErrors({
          general: errorMessage
        });
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Member Lookup Step
  if (step === 'lookup') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className={`max-w-2xl mx-auto ${mobileClasses.container} py-6 sm:py-8`}>
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className={`${mobileClasses.flexMobile} items-center mb-4`}>
              <button
                onClick={() => navigate('/app/complaints')}
                className={`${mobileClasses.flexMobile} items-center text-gray-600 hover:text-gray-900 ${isMobile() ? 'mb-2' : 'mr-4'}`}
              >
                <ArrowLeft className={`${isMobile() ? 'h-4 w-4' : 'h-5 w-5'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
                <span className={isMobile() ? 'text-sm' : ''}>Back to Complaints</span>
              </button>
            </div>
            <h1 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900`}>Submit New Complaint</h1>
            <p className={`mt-2 ${mobileClasses.textMobile} text-gray-600`}>
              First, please enter the Member ID or Phone Number to retrieve member details.
            </p>
          </div>

          {/* Member Lookup Form */}
          <div className="bg-white shadow rounded-lg">
            <form onSubmit={handleMemberLookup} className={`${mobileClasses.cardMobile} ${mobileClasses.formMobile}`}>
              <div className="mb-6">
                <label className={`block ${mobileClasses.textMobile} font-medium text-gray-700 mb-2`}>
                  <Search className="inline h-4 w-4 mr-1" />
                  Member ID or Phone Number *
                </label>
                <input
                  type="text"
                  value={memberIdentifier}
                  onChange={(e) => {
                    setMemberIdentifier(e.target.value);
                    if (errors.memberLookup) {
                      setErrors(prev => ({ ...prev, memberLookup: undefined }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.memberLookup ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter Member ID (e.g., NF20250001) or Phone Number"
                  autoFocus
                />
                {errors.memberLookup && (
                  <p className={`mt-1 ${mobileClasses.textMobile} text-red-600`}>{errors.memberLookup}</p>
                )}
                <p className={`mt-2 ${mobileClasses.textMobile} text-gray-500`}>
                  You can search by Member ID or registered phone number.
                </p>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/app/complaints')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  disabled={isLookingUpMember}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLookingUpMember}
                  className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLookingUpMember ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search Member
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className={`max-w-md w-full bg-white rounded-lg shadow-lg ${mobileClasses.cardMobile} text-center`}>
          <CheckCircle className={`mx-auto ${isMobile() ? 'h-12 w-12' : 'h-16 w-16'} text-green-500 mb-4`} />
          <h2 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900 mb-2`}>Complaint Submitted Successfully!</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className={`${mobileClasses.textMobile} text-gray-600 mb-2`}>
              Your complaint has been registered with the following number:
            </p>
            <p className={`${mobileClasses.textMobileXLarge} font-bold text-blue-600`}>
              {complaintNumber}
            </p>
          </div>
          <p className={`${mobileClasses.textMobile} text-gray-600 mb-6`}>
            Please save this complaint number for future reference. 
            You will receive updates via email and SMS.
          </p>
          <div className={`${mobileClasses.flexMobile} ${isMobile() ? 'space-y-3' : 'space-x-3'}`}>
            <button
              onClick={() => navigate('/app/complaints')}
              className={`${isMobile() ? 'w-full' : 'flex-1'} bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700`}
            >
              View All Complaints
            </button>
            <button
              onClick={() => {
                setStep('lookup');
                setMemberIdentifier('');
                setMemberDetails(null);
                setComplaintNumber('');
        setFormData({
          title: '',
          description: '',
          category_id: '',
          department_id: '',
          priority: 'medium',
          location: '',
          address: '',
          use_member_address: true,
          contact_phone: '',
          use_member_phone: true,
          preferred_contact_time: '',
          attachments: [],
          user_id: ''
        });
                setAvailableDepartments([]);
                setSelectedFiles([]);
                setErrors({});
              }}
              className={`${isMobile() ? 'w-full' : 'flex-1'} bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300`}
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complaint Form Step
  return (
    <div className="min-h-screen bg-gray-50">
      <div className={`max-w-4xl mx-auto ${mobileClasses.container} py-6 sm:py-8`}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className={`${mobileClasses.flexMobile} items-center mb-4`}>
            <button
              onClick={() => setStep('lookup')}
              className={`${mobileClasses.flexMobile} items-center text-gray-600 hover:text-gray-900 ${isMobile() ? 'mb-2' : 'mr-4'}`}
            >
              <ArrowLeft className={`${isMobile() ? 'h-4 w-4' : 'h-5 w-5'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
              <span className={isMobile() ? 'text-sm' : ''}>Back to Member Lookup</span>
            </button>
          </div>
          <h1 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900`}>Submit New Complaint</h1>
          <p className={`mt-2 ${mobileClasses.textMobile} text-gray-600`}>
            Please provide detailed information about your complaint. All fields marked with * are required.
          </p>
        </div>

        {/* Member Details Card */}
        {memberDetails && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className={`${mobileClasses.textMobile} font-semibold text-gray-900 mb-3 flex items-center`}>
              <UserCircle className="h-5 w-5 mr-2 text-blue-600" />
              Member Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <User className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className={`${mobileClasses.textMobile} font-medium text-gray-900`}>
                    {memberDetails.first_name} {memberDetails.last_name}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <UserCircle className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                <div>
                  <p className="text-xs text-gray-500">Member ID</p>
                  <p className={`${mobileClasses.textMobile} font-medium text-gray-900`}>
                    {memberDetails.member_id}
                  </p>
                </div>
              </div>
              {memberDetails.phone && (
                <div className="flex items-start">
                  <Phone className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className={`${mobileClasses.textMobile} font-medium text-gray-900`}>
                      {memberDetails.phone}
                    </p>
                  </div>
                </div>
              )}
              {memberDetails.email && (
                <div className="flex items-start">
                  <Mail className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className={`${mobileClasses.textMobile} font-medium text-gray-900`}>
                      {memberDetails.email}
                    </p>
                  </div>
                </div>
              )}
              {(memberDetails.block_number || memberDetails.flat_number) && (
                <div className="flex items-start">
                  <Home className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className={`${mobileClasses.textMobile} font-medium text-gray-900`}>
                      {memberDetails.block_number && memberDetails.flat_number 
                        ? `Block ${memberDetails.block_number}, Flat ${memberDetails.flat_number}`
                        : memberDetails.address || 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className={`${mobileClasses.cardMobile} ${mobileClasses.formMobile}`}>
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              </div>
            )}

            {/* Category - Step 1: Select Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              {isLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  Loading categories...
                </div>
              ) : (
                <select
                  value={formData.category_id}
                  onChange={(e) => handleInputChange('category_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.category_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.category_id && (
                <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
              )}
            </div>

            {/* Department - Step 2: Shown only when category is selected */}
            {formData.category_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                {availableDepartments.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                    No departments available for this category
                  </div>
                ) : (
                  <select
                    value={formData.department_id}
                    onChange={(e) => handleInputChange('department_id', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.department_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a department</option>
                    {availableDepartments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.department_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.department_id}</p>
                )}
              </div>
            )}

            {/* Title - Step 3: Complaint Title */}
            <div>
              <label className={`block ${mobileClasses.textMobile} font-medium text-gray-700 mb-2`}>
                Complaint Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`${mobileClasses.inputMobile} border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Brief description of the issue"
                maxLength={100}
              />
              {errors.title && (
                <p className={`mt-1 ${mobileClasses.textMobile} text-red-600`}>{errors.title}</p>
              )}
            </div>

            {/* Description - Step 4: Detailed Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detailed Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Please provide a detailed description of the problem, including when it started, how it affects you, and any other relevant details..."
                maxLength={1000}
              />
              <div className="mt-1 flex justify-between text-sm text-gray-500">
                <span>{errors.description && <span className="text-red-600">{errors.description}</span>}</span>
                <span>{formData.description.length}/1000</span>
              </div>
            </div>

            {/* Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low - Can wait</option>
                  <option value="medium">Medium - Normal priority</option>
                  <option value="high">High - Urgent attention needed</option>
                  <option value="urgent">Urgent - Immediate action required</option>
                </select>
                <div className="mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(formData.priority)}`}>
                    {formData.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-1" />
                Complaint Address *
              </label>
              
              {/* Use Member Address Option */}
              {memberDetails && (
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.use_member_address}
                      onChange={(e) => {
                        const useMemberAddress = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          use_member_address: useMemberAddress,
                          location: useMemberAddress ? prev.address : prev.location,
                          address: useMemberAddress ? prev.address : prev.address
                        }));
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Use member's address ({memberDetails.block_number && memberDetails.flat_number 
                        ? `Block ${memberDetails.block_number}, House ${memberDetails.flat_number}`
                        : memberDetails.address || 'N/A'})
                    </span>
                  </label>
                </div>
              )}

              {/* Address Input - shown when not using member's address */}
              {!formData.use_member_address && (
                <div>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => {
                      const newAddress = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        address: newAddress,
                        location: newAddress
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.location ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter complaint address (e.g., Block A, Floor 2, Common Area, Parking Lot)"
                    required
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                  )}
                </div>
              )}

              {/* Show member's address when using it */}
              {formData.use_member_address && memberDetails && (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {formData.address || 'Member address will be used'}
                </div>
              )}
            </div>

            {/* Contact Phone Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline h-4 w-4 mr-1" />
                Contact Phone Number *
              </label>
              
              {/* Use Member Phone Option */}
              {memberDetails && memberDetails.phone && (
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.use_member_phone}
                      onChange={(e) => {
                        const useMemberPhone = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          use_member_phone: useMemberPhone,
                          contact_phone: useMemberPhone ? (memberDetails.phone || '') : prev.contact_phone
                        }));
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Use member's phone number ({memberDetails.phone})
                    </span>
                  </label>
                </div>
              )}

              {/* Phone Input - shown when not using member's phone */}
              {!formData.use_member_phone && (
                <div>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter contact phone number"
                    required
                  />
                </div>
              )}

              {/* Show member's phone when using it */}
              {formData.use_member_phone && memberDetails && memberDetails.phone && (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {memberDetails.phone}
                </div>
              )}
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Attachments (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Click to upload files or drag and drop
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    PNG, JPG, PDF, TXT up to 5MB each
                  </span>
                </label>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                        <div className="flex items-center">
                          <ImageIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-700">{file.name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setStep('lookup')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Submit Complaint
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewComplaintPage;
